import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, finalize, forkJoin, map, of, take } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GetLinkedSitesByGroup,
  LocationGroup,
} from 'src/app/core/models/admin/location-admin.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { BANNER_TYPE } from '../../banner/banner.constants';
import { IMAGE_SRC, STATUS_OPTIONS } from '../../shared/shared-constants';
import { AddGroupComponent } from './add-group/add-group.component';
import {
  ColumnIds,
  ColumnText,
  DISPLAY_TEXT,
  DISPLAYED_COLUMNS,
  EXCEL_COLUMNS,
  EXCEL_FILE_NAME,
  LocationGroupExcelDataType,
  LocationGroupTableData,
} from './location-group.constants';

@Component({
  selector: 'app-location-group',
  templateUrl: './location-group.component.html',
  styleUrls: ['./location-group.component.scss'],
  standalone: false,
})
export class LocationGroupComponent implements OnInit {
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  private destroy = inject(DestroyRef);
  private userService = inject(UserService);
  public readonly commonUtils = inject(CommonUtils);
  private locationService = inject(LocationService);
  private dialog = inject(MatDialog);

  readonly CONSTANTS = DISPLAY_TEXT;
  readonly COLUMNTEXT = ColumnText;
  readonly IMAGE_SRC = IMAGE_SRC;
  readonly SELECT_ALL_TEXT = STATUS_OPTIONS.ALL_SELECTED;
  readonly displayedColumns = DISPLAYED_COLUMNS;

  // Column Search Variables
  groupData = this.commonUtils.createTableColumn(ColumnIds.GROUP);
  descriptionData = this.commonUtils.createTableColumn(ColumnIds.DESCRIPTION);
  noOfSiteData = this.commonUtils.createTableColumn(ColumnIds.NOOFSITES);

  selectedGroup!: LocationGroupTableData;
  locationGroups!: LocationGroup[];
  selectedIndex!: number;
  dataSource = new MatTableDataSource<LocationGroupTableData>([]);
  originalLocationGroupTable: LocationGroup[] = [];
  isDeleteDisabled = true;

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(() => {
          this.getLocationGroup();
          this.clearAllFilters();
        }),
      )
      .subscribe();
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.originalLocationGroupTable as unknown as LocationGroupTableData[],
      [
        this.descriptionData.formControl,
        this.groupData.formControl,
        this.noOfSiteData.formControl,
      ],
    );
  }

  getLocationGroup() {
    //Since this call takes a while on initial page load, make sure loading modal shows the whole time
    this.commonUtils.updateLoadingModal(true);
    this.commonUtils.lockUpdateLoadingModal(true);

    this.locationService
      .getLocationGroups()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((data) => {
          this.locationGroups = data?.response?.map((group: LocationGroup) => {
            return {
              ...group,
              checked: false,
            };
          });

          this.originalLocationGroupTable = this.locationGroups;
          this.setTableData(this.locationGroups);
          this.sort.sort({
            id: 'siteCount',
            start: 'asc',
            disableClear: true,
          });
        }),
      )
      .subscribe(() => {
        this.dataSource.filteredData[0].checked = true;
        //Unlock loading modals
        this.commonUtils.lockUpdateLoadingModal(false);
        this.commonUtils.updateLoadingModal(false);
      });
  }

  setTableData(locationGroups: LocationGroup[]) {
    this.dataSource.data =
      locationGroups as unknown as LocationGroupTableData[];
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  getLinkedSite(locationGroup: LocationGroupTableData, index: number) {
    this.selectedIndex = index;
    this.locationService
      .getLinkedSitesByGroup(locationGroup.code)
      .pipe(take(1))
      .subscribe((resp) => {
        this.selectedGroup = {
          ...locationGroup,
          linkedSites: resp.response?.linkedSitesPerGroup,
        };
      });
  }

  exportExcel(): void {
    this.commonUtils.updateLoadingModal(true);
    const excelData: LocationGroupExcelDataType[] = [];
    const codes$ = this.dataSource.filteredData.map(
      (value: LocationGroupTableData) =>
        this.locationService.getLinkedSitesByGroup(value.code).pipe(take(1)),
    );
    forkJoin(codes$)
      .pipe(
        take(1),
        catchError(() => {
          this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_UNSUCCESSFUL);
          return of(false);
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      )
      .subscribe((values) => {
        if (values !== false) {
          (values as GetLinkedSitesByGroup[]).forEach(
            (linkedSites, index: number) => {
              linkedSites.response.linkedSitesPerGroup.forEach((linkedSite) => {
                excelData.push({
                  [ColumnText.GROUP]: this.dataSource.filteredData[index]?.code,
                  [ColumnText.DESCRIPTION_COL]:
                    this.dataSource.filteredData[index]?.name,
                  [ColumnText.LINKED_SITES]: linkedSite?.name,
                  [ColumnText.SLIC]: linkedSite?.slic,
                });
              });
            },
          );

          this.commonUtils.exportExcel(
            EXCEL_COLUMNS,
            EXCEL_FILE_NAME,
            excelData,
            ['action', 'version', 'checked', 'id'],
          );
        }
      });
  }

  search() {
    this.commonUtils.searchFilter(
      [this.groupData, this.descriptionData, this.noOfSiteData],
      this.dataSource,
    );
  }

  addGroup() {
    this.dialog
      .open(AddGroupComponent, {
        minWidth: '16.125rem',
      })
      .afterClosed()
      .subscribe((value) => {
        if (value) {
          this.getLocationGroup();
        }
      });
  }

  get isFilterEnabled() {
    return (
      this.groupData.formControl.value ||
      this.noOfSiteData.formControl.value ||
      this.descriptionData.formControl.value
    );
  }
}
